/**
 * Admin header "● N online" live-presence chip — Feb 2026.
 *
 * Sits in the admin portal header (see `PortalShell.jsx`) and polls
 * `GET /api/admin/presence/online` every 30 seconds. Renders:
 *
 *   • A subtle green-dot chip: "● 6 online"
 *   • On click, a popover breaks out the count by role bucket and
 *     lists the currently online users with the time since their
 *     last heartbeat.
 *
 * Self-hides for non-admin users.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_INTERVAL_MS = 30_000;

function fmtAgo(secondsAgo) {
  if (secondsAgo == null) return "just now";
  if (secondsAgo < 15)  return "just now";
  if (secondsAgo < 60)  return `${secondsAgo}s ago`;
  const m = Math.floor(secondsAgo / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

const BUCKETS = [
  { key: "partners",        label: "Partners" },
  { key: "customers",       label: "Customers" },
  { key: "lab_technicians", label: "Lab" },
  { key: "admins",          label: "Admins" },
  { key: "others",          label: "Other" },
];

export function AdminPresenceChip() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const popRef = useRef(null);

  const isAdmin = user && user.role === "admin";

  // Feb 2026 — click a user in the popover to jump to their admin drawer.
  // Partners go to the PWS Partner Network page (with a query param the
  // page reads to auto-open the drawer). Customers / lab / other admins
  // don't have first-class detail drawers yet — they go to the User
  // Management page filtered by email so ops can find them fast.
  const jumpToUser = (u) => {
    setOpen(false);
    if (u.role === "partner" && u.partner_id) {
      navigate(`/admin/partners/network?open=${encodeURIComponent(u.partner_id)}`);
    } else {
      navigate(`/admin/users?q=${encodeURIComponent(u.email || "")}`);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = () => {
      api.get("/admin/presence/online")
        .then(({ data }) => { if (!cancelled) setData(data); })
        .catch(() => { /* silent — chip just shows "—" */ })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const total = data?.total ?? 0;
  const users = data?.users ?? [];
  const breakdown = data?.breakdown ?? {};

  // Group users under their bucket for the popover list.
  const grouped = useMemo(() => {
    const bucketsById = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
    for (const u of users) {
      const role = u.role || "";
      const key = ({
        admin: "admins", partner: "partners", lab_technician: "lab_technicians",
        customer: "customers", member: "customers",
      })[role] || "others";
      bucketsById[key].push(u);
    }
    return bucketsById;
  }, [users]);

  if (!isAdmin) return null;

  return (
    <div ref={popRef} className="relative flex-shrink-0" data-testid="admin-presence-chip-wrap">
      <button
        type="button"
        data-testid="admin-presence-chip"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={loading ? "Loading live user count…" : `${total} user${total === 1 ? "" : "s"} online`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors"
      >
        <span
          data-testid="admin-presence-chip-dot"
          className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.15)] animate-pulse"
        />
        <span data-testid="admin-presence-chip-count">
          {loading ? "…" : `${total} online`}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          data-testid="admin-presence-popover"
          className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-slate-200 bg-white shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600">Live presence</div>
              <div className="font-semibold text-slate-900 text-sm mt-0.5" data-testid="admin-presence-total">
                {total} online right now
              </div>
            </div>
            <Users className="w-4 h-4 text-slate-400" />
          </div>

          {/* Breakdown row */}
          <div className="px-4 py-2 flex flex-wrap gap-x-3 gap-y-1 border-b border-slate-100 bg-slate-50/60">
            {BUCKETS.map((b) => {
              const n = breakdown[b.key] ?? 0;
              if (n === 0 && b.key === "others") return null;
              return (
                <span
                  key={b.key}
                  data-testid={`admin-presence-bucket-${b.key}`}
                  className="text-[11px] font-medium text-slate-600"
                >
                  <span className="font-semibold text-slate-900">{n}</span> {b.label}
                </span>
              );
            })}
          </div>

          <div className="max-h-80 overflow-y-auto py-1">
            {total === 0 && (
              <div data-testid="admin-presence-empty" className="px-4 py-6 text-center text-xs text-slate-500">
                No one else is currently active.
              </div>
            )}
            {BUCKETS.map((b) => {
              const rows = grouped[b.key] || [];
              if (rows.length === 0) return null;
              return (
                <div key={b.key} className="mb-1">
                  <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                    {b.label} · {rows.length}
                  </div>
                  {rows.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      data-testid={`admin-presence-user-${u.id}`}
                      onClick={() => jumpToUser(u)}
                      title={`Jump to ${u.full_name || u.email}'s admin record`}
                      className="w-full px-4 py-1.5 flex items-center justify-between hover:bg-slate-50 text-left"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate" title={u.full_name}>
                          {u.full_name || u.email}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {u.partner_business_name
                            ? `${u.role_label} · ${u.partner_business_name}`
                            : u.role_label}
                        </div>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 pl-2 flex-shrink-0">
                        {fmtAgo(u.seconds_ago)}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="px-4 py-2 border-t border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
            Refreshes every 30s · {data?.online_window_seconds ?? 120}s window
          </div>
        </div>
      )}
    </div>
  );
}
